import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { Certificate, CertificateX509Details, ZerosslOrder } from '../types/models/Certificate';
import type { ApiResponseCertificateShow } from '../types/api/ApiResponseCertificate';

interface UseCertificateReturn {
  certificate: Certificate | null;
  x509: CertificateX509Details | null;
  /** Null on every non-ZeroSSL row (and on synced ZeroSSL rows without an order). */
  zerosslOrder: ZerosslOrder | null;
  loading: boolean;
  error: string | null;
  notFound: boolean;
  refetch: () => Promise<void>;
}

/**
 * Fetch a certificate row + its parsed x509 and zerossl_order siblings
 * (GET /datacenters/certificates/{id}).
 *
 * Entity state is dropped the moment `certificateId` changes (so a previous
 * certificate can never render — or key child tabs — under the new URL), and
 * out-of-order responses are discarded via a request sequence, while a
 * same-id `refetch` keeps the loaded row visible during the reload
 * (stale-while-refetch, for the title-bar Refresh button).
 */
export const useCertificate = (certificateId: number): UseCertificateReturn => {
  const [certificate, setCertificate] = useState<Certificate | null>(null);
  const [x509, setX509] = useState<CertificateX509Details | null>(null);
  const [zerosslOrder, setZerosslOrder] = useState<ZerosslOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const requestSeqRef = useRef(0);
  const lastIdRef = useRef(certificateId);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchCertificate = useCallback(async () => {
    const seq = ++requestSeqRef.current;
    try {
      setLoading(true);
      setError(null);
      setNotFound(false);

      const response = await apiFetch(`/datacenters/certificates/${certificateId}`);
      if (seq !== requestSeqRef.current) {
        return;
      }

      if (response.status === 404) {
        // Clear any previously loaded row so a certificate pruned since the
        // page loaded cannot keep rendering under the not-found state.
        setCertificate(null);
        setX509(null);
        setZerosslOrder(null);
        setNotFound(true);
        return;
      }

      let body: ApiResponseCertificateShow | null;
      try {
        body = (await response.json()) as ApiResponseCertificateShow;
      } catch {
        body = null;
      }
      if (seq !== requestSeqRef.current) {
        return;
      }

      if (body?.status === 'success' && body.data?.certificate) {
        setCertificate(body.data.certificate);
        setX509(body.data.x509 ?? null);
        setZerosslOrder(body.data.zerossl_order ?? null);
        return;
      }

      setError(
        body?.message ?? `Failed to load certificate (HTTP ${`${response.status} ${response.statusText}`.trim()})`,
      );
    } catch (err) {
      if (seq === requestSeqRef.current) {
        setError(err instanceof Error ? err.message : 'An error occurred while fetching the certificate');
      }
    } finally {
      if (seq === requestSeqRef.current) {
        setLoading(false);
      }
    }
  }, [apiFetch, certificateId]);

  // Declared before the fetch effect so the reset runs first on id change.
  useEffect(() => {
    if (lastIdRef.current === certificateId) {
      return;
    }
    lastIdRef.current = certificateId;
    setCertificate(null);
    setX509(null);
    setZerosslOrder(null);
    setError(null);
    setNotFound(false);
  }, [certificateId]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      void fetchCertificate();
    }
  }, [auth.isAuthenticated, fetchCertificate]);

  return { certificate, x509, zerosslOrder, loading, error, notFound, refetch: fetchCertificate };
};
